<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><app-button @click.native="onShowModal(undefined)">Add bank</app-button></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__short_name><div class="table-default__left">Short name</div></template>
        <template #h__swift><div class="table-default__left">SWIFT</div></template>
        <template #h__address><div class="table-default__left">Address</div></template>
        <template #count="{ index }">{{ index }}</template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #short_name="{ row }"><div class="table-default__left">{{ row.short_name }}</div></template>
        <template #swift="{ row }"><div class="table-default__left">{{ row.swift }}</div></template>
        <template #address="{ row }"><div class="table-default__left">{{ row.address || '-' }}</div></template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="edit-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('edit-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ !item_to_update ? 'Add' : 'Update' }} bank</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="name" required>
              <app-input
                v-model="form.name"
                placeholder="Name"
                id="name"
                name="name"
                :error="$v.form.name.$error"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Short name" label-for="short_name" required>
              <app-input
                v-model="form.short_name"
                placeholder="Short name"
                id="short_name"
                name="short_name"
                :error="$v.form.short_name.$error"
              />
              <template #error>
                <div v-if="$v.form.short_name.$dirty && !$v.form.short_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Swift" label-for="swift" required>
              <app-input
                v-model="form.swift"
                placeholder="Swift"
                id="swift"
                name="swift"
                :error="$v.form.swift.$error"
              />
              <template #error>
                <div v-if="$v.form.swift.$dirty && !$v.form.swift.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Address" label-for="address" required>
              <app-input
                v-model="form.address"
                placeholder="Address"
                id="address"
                name="address"
                :error="$v.form.address.$error"
              />
              <template #error>
                <div v-if="$v.form.address.$dirty && !$v.form.address.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit" :disabled="$v.form.$error">{{ !item_to_update ? 'Add' : 'Save' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete item
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this item?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="deleteItem">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { getFinanceBanks, postFinanceBank, patchFinanceBank, deleteFinanceBank } from '@/http'

export default {
  name: 'FinanceDictionariesBanks',
  data() {
    return {
      page_loaded: false,
      item_to_delete: null,
      item_to_update: null,
      table_preloader: false,
      form: {},
      table: {
        items: [],
        columns: [
          'count',
          'name',
          'short_name',
          'swift',
          'address',
          'controls'
        ],
        options: {
          headings: {
            'count': '#',
            'name': 'Name',
            'short_name': 'Short name',
            'swift': 'SWIFT',
            'address': 'Address',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  validations: {
    form: {
      name: { required },
      short_name: { required },
      swift: { required },
      address: { required },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getFinanceBanks({ page: this.pagination.page, page_size: 20 })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.page_loaded = true
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      const data = {
        name: this.form.name,
        short_name: this.form.short_name,
        swift: this.form.swift,
        address: this.form.address,
      }
      if (this.item_to_update) {
        patchFinanceBank(this.item_to_update, data)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Item updated',
            })
            this.$modal.hide('edit-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      } else {
        postFinanceBank(this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Item created',
            })
            this.$modal.hide('edit-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      }
    },
    onDeleteItem() {
      this.$refs.deleteItem.preload = true
      deleteFinanceBank(this.item_to_delete)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Item deleted',
          })
          this.$modal.hide('delete-modal')
        })
        .catch(() => {
          this.$refs.deleteItem.preload = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal(data = {}) {
      this.item_to_update = data.id || null
      this.form = data
      this.$v.form.$reset()
      this.$modal.show('edit-modal')
    },
    onShowDeleteModal(item_id) {
      this.item_to_delete = item_id
      this.$modal.show('delete-modal')
    },
  },
}
</script>
